package com.htmlagent.site.state.brief

import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.max
import kotlin.math.min
import kotlin.math.pow
import kotlin.math.round
import kotlin.math.sin
import kotlin.math.sqrt

/**
 * A colour in OkLCH: lightness 0..1, chroma, hue in degrees.
 *
 * The whole palette is derived here and not in HSL because HSL's lightness is not perceptual:
 * two colours at the same `L` look nothing alike, so a ramp built on it comes out with steps
 * that jump in some hues and stall in others.
 */
data class Oklch(val lightness: Double, val chroma: Double, val hue: Double)

/**
 * One step of a derived scale: the colour and the job it does.
 *
 * The role travels with the value because it is the whole point of a twelve-step scale — the
 * agent is told "step 9 is the solid", not "here are twelve browns".
 */
data class Step(val index: Int, val hex: String, val role: StepRole)

/** What a step of the scale is for, in the Radix model the rest of the project already uses. */
enum class StepRole {
    Background,
    Surface,
    SurfaceHover,
    SurfaceActive,
    Divider,
    Border,
    ControlBorder,
    Solid,
    SolidHover,
    Text,
    Heading,
    None,
}

/**
 * Derives a whole system from one colour, which is the only place where the assistant decides
 * instead of asking.
 *
 * A person can pick a colour they like; nobody can pick twelve that hold together. So the seed
 * is converted to OkLCH, walked up a fixed lightness ladder keeping its hue, and its chroma is
 * lowered until every step fits inside sRGB — a step that clipped would come back a different
 * hue and break the family. The neutrals are the same hue with the chroma nearly off, because a
 * grey leaning slightly towards the accent looks chosen and `#808080` looks like a template.
 */
object Palette {

    /** Lightness of each step over a light canvas. Index 8 and 9 are replaced by the seed's. */
    private val LIGHT_LIGHTNESS = listOf(
        0.993, 0.981, 0.962, 0.941, 0.917, 0.886, 0.842, 0.775, 0.0, 0.0, 0.505, 0.330,
    )

    /** Chroma of each step as a fraction of the seed's. It peaks at the solid. */
    private val LIGHT_CHROMA = listOf(
        0.028, 0.055, 0.095, 0.135, 0.180, 0.235, 0.330, 0.520, 1.00, 0.97, 0.88, 0.55,
    )

    /** The same ladder read from the other side, for a page that goes dark. */
    private val DARK_LIGHTNESS = listOf(
        0.178, 0.213, 0.255, 0.291, 0.325, 0.372, 0.437, 0.542, 0.0, 0.0, 0.800, 0.930,
    )

    private val DARK_CHROMA = listOf(
        0.030, 0.055, 0.095, 0.130, 0.165, 0.210, 0.280, 0.400, 0.95, 0.90, 0.62, 0.35,
    )

    private val NEUTRAL_LIGHTNESS = listOf(
        0.993, 0.977, 0.958, 0.939, 0.919, 0.894, 0.856, 0.780, 0.640, 0.598, 0.470, 0.238,
    )

    private val ROLES = listOf(
        StepRole.Background, StepRole.Background, StepRole.Surface, StepRole.SurfaceHover,
        StepRole.SurfaceActive, StepRole.Divider, StepRole.Border, StepRole.ControlBorder,
        StepRole.Solid, StepRole.SolidHover, StepRole.Text, StepRole.Heading,
    )

    /** How much of the accent's chroma survives into the neutrals. */
    private const val NEUTRAL_TINT_SHARE = 0.07

    /** And the ceiling, so a very saturated accent does not produce coloured greys. */
    private const val NEUTRAL_TINT_MAX = 0.016

    /** The contrast a body text has to reach over its background. */
    const val AA_BODY = 4.5

    /** The contrast the outline of something usable has to reach. */
    const val AA_NON_TEXT = 3.0

    /**
     * The band a hue has to be justified to sit in: it is where the orange reflex lands.
     *
     * Measured on the HSL wheel and not on OkLCH's, because that is the wheel the rule was
     * written against and the one whose numbers a person recognises. The two do not line up —
     * the same terracotta reads 13° on one and 37° on the other — so mixing them would either
     * flag every warm colour or none.
     */
    val SUSPECT_HUE = 15.0..35.0

    /** `#rrggbb` or `#rgb`, with or without the hash, to OkLCH. `null` if it is not a colour. */
    fun parse(hex: String): Oklch? {
        val rgb = toRgb(hex) ?: return null
        val (lightness, a, b) = toOklab(rgb)
        val hue = atan2(b, a) * HALF_TURN / PI
        return Oklch(lightness, sqrt(a * a + b * b), if (hue < 0) hue + FULL_TURN else hue)
    }

    /**
     * The same colour written as `#RRGGBB`, or `null` if it is not one.
     *
     * `input type="color"` accepts nothing else — not the short form, not a half-typed hex — and
     * silently falls back to black rather than refusing, so the caller has to check first.
     */
    fun normalize(hex: String): String? = toRgb(hex)?.let { format(it) }

    /** Back to `#RRGGBB`, walking the chroma down until the colour fits inside sRGB. */
    fun toHex(colour: Oklch): String {
        val radians = colour.hue * PI / HALF_TURN
        repeat(GAMUT_STEPS + 1) { attempt ->
            val chroma = colour.chroma * (1.0 - attempt.toDouble() / GAMUT_STEPS)
            val rgb = toRgb(colour.lightness, chroma * cos(radians), chroma * sin(radians))
            if (rgb.all { it >= -GAMUT_SLACK && it <= 1.0 + GAMUT_SLACK }) return format(rgb)
        }
        return format(toRgb(colour.lightness, 0.0, 0.0))
    }

    /** The accent's twelve steps over a light canvas. Step 9 is the colour that was chosen. */
    fun accentRamp(seed: Oklch): List<Step> = ramp(seed, LIGHT_LIGHTNESS, LIGHT_CHROMA, light = true)

    /** The same accent over a dark canvas: the solid has to rise or it disappears into it. */
    fun darkRamp(seed: Oklch): List<Step> = ramp(seed, DARK_LIGHTNESS, DARK_CHROMA, light = false)

    /** The neutrals, carrying just enough of the accent's hue to look like they belong to it. */
    fun neutralRamp(seed: Oklch): List<Step> {
        val tint = min(seed.chroma * NEUTRAL_TINT_SHARE, NEUTRAL_TINT_MAX)
        return NEUTRAL_LIGHTNESS.mapIndexed { index, lightness ->
            Step(index + 1, toHex(Oklch(lightness, tint, seed.hue)), ROLES[index])
        }
    }

    /** How much of the accent's hue the greys keep, so the brief can state it. */
    fun neutralTint(seed: Oklch): Double = min(seed.chroma * NEUTRAL_TINT_SHARE, NEUTRAL_TINT_MAX)

    /** WCAG contrast ratio between two colours, 1.0 when either is not a colour. */
    fun contrast(one: String, other: String): Double {
        val a = luminance(one) ?: return 1.0
        val b = luminance(other) ?: return 1.0
        return (max(a, b) + LUMINANCE_OFFSET) / (min(a, b) + LUMINANCE_OFFSET)
    }

    /**
     * Which of the two candidates goes on top of the solid.
     *
     * Measured, not assumed: on a mid-lightness accent white and the accent's own darkest tone
     * can be within a tenth of each other, and picking by eye gets it wrong about as often as
     * not.
     */
    fun onSolid(solid: String, ink: String): String =
        if (contrast(solid, WHITE) >= contrast(solid, ink)) WHITE else ink

    /** Pure white, which is one of the two candidates and also the floor a veil is measured on. */
    const val WHITE = "#FFFFFF"

    private const val GAMUT_STEPS = 32
    private const val GAMUT_SLACK = 0.002
    private const val LUMINANCE_OFFSET = 0.05
    private const val HALF_TURN = 180.0
    private const val FULL_TURN = 360.0

    /** Over a dark canvas the solid has to rise, or a dark accent sinks into the background. */
    private const val DARK_SOLID_FLOOR = 0.64
    private const val DARK_SOLID_CEILING = 0.80
    private const val DARK_SOLID_RISE = 0.18

    /** The hover step: one notch down over light, one notch up over dark. */
    private const val LIGHT_HOVER_DROP = 0.045
    private const val LIGHT_HOVER_FLOOR = 0.16
    private const val DARK_HOVER_RISE = 0.06
    private const val DARK_HOVER_CEILING = 0.86

    private fun ramp(seed: Oklch, lightness: List<Double>, chroma: List<Double>, light: Boolean): List<Step> {
        val solid = if (light) {
            seed.lightness
        } else {
            min(DARK_SOLID_CEILING, max(DARK_SOLID_FLOOR, seed.lightness + DARK_SOLID_RISE))
        }
        val hover = if (light) {
            max(LIGHT_HOVER_FLOOR, solid - LIGHT_HOVER_DROP)
        } else {
            min(DARK_HOVER_CEILING, solid + DARK_HOVER_RISE)
        }
        return lightness.mapIndexed { index, step ->
            val level = when (index) {
                SOLID_INDEX -> solid
                HOVER_INDEX -> hover
                else -> step
            }
            Step(index + 1, toHex(Oklch(level, seed.chroma * chroma[index], seed.hue)), ROLES[index])
        }
    }

    private const val SOLID_INDEX = 8
    private const val HOVER_INDEX = 9

    private fun luminance(hex: String): Double? {
        val (r, g, b) = toRgb(hex) ?: return null
        return LUMA_R * toLinear(r) + LUMA_G * toLinear(g) + LUMA_B * toLinear(b)
    }

    private const val LUMA_R = 0.2126
    private const val LUMA_G = 0.7152
    private const val LUMA_B = 0.0722

    private fun toRgb(hex: String): Triple<Double, Double, Double>? {
        val raw = hex.trim().removePrefix("#")
        val full = if (raw.length == SHORT_HEX) raw.map { "$it$it" }.joinToString("") else raw
        if (full.length != FULL_HEX || full.any { it.digitToIntOrNull(HEX_RADIX) == null }) return null
        fun channel(at: Int) = full.substring(at, at + HEX_DIGITS).toInt(HEX_RADIX) / MAX_CHANNEL
        return Triple(channel(RED_AT), channel(GREEN_AT), channel(BLUE_AT))
    }

    private const val RED_AT = 0
    private const val GREEN_AT = 2
    private const val BLUE_AT = 4
    private const val SHORT_HEX = 3
    private const val FULL_HEX = 6
    private const val HEX_RADIX = 16
    private const val HEX_DIGITS = 2
    private const val MAX_CHANNEL = 255.0

    private fun format(rgb: Triple<Double, Double, Double>): String {
        fun channel(value: Double): String {
            val level = round(min(1.0, max(0.0, value)) * MAX_CHANNEL).toInt()
            return level.toString(HEX_RADIX).uppercase().padStart(HEX_DIGITS, '0')
        }
        return "#${channel(rgb.first)}${channel(rgb.second)}${channel(rgb.third)}"
    }

    private fun toLinear(channel: Double): Double =
        if (channel <= SRGB_KNEE) channel / SRGB_SLOPE else ((channel + SRGB_OFFSET) / SRGB_SCALE).pow(SRGB_GAMMA)

    private fun toGamma(channel: Double): Double =
        if (channel <= LINEAR_KNEE) SRGB_SLOPE * channel else SRGB_SCALE * channel.pow(1.0 / SRGB_GAMMA) - SRGB_OFFSET

    private const val SRGB_KNEE = 0.04045
    private const val LINEAR_KNEE = 0.0031308
    private const val SRGB_SLOPE = 12.92
    private const val SRGB_OFFSET = 0.055
    private const val SRGB_SCALE = 1.055
    private const val SRGB_GAMMA = 2.4

    private const val CUBE = 3.0

    private fun cubeRoot(value: Double): Double =
        if (value < 0) -((-value).pow(1.0 / CUBE)) else value.pow(1.0 / CUBE)

    @Suppress("MagicNumber") // The OkLab matrices. They are the definition, not a choice.
    private fun toOklab(rgb: Triple<Double, Double, Double>): Triple<Double, Double, Double> {
        val r = toLinear(rgb.first)
        val g = toLinear(rgb.second)
        val b = toLinear(rgb.third)
        val l = cubeRoot(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
        val m = cubeRoot(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
        val s = cubeRoot(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
        return Triple(
            0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
            1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
            0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
        )
    }

    @Suppress("MagicNumber") // The inverse of the same matrices.
    private fun toRgb(lightness: Double, a: Double, b: Double): Triple<Double, Double, Double> {
        val l = (lightness + 0.3963377774 * a + 0.2158037573 * b).let { it * it * it }
        val m = (lightness - 0.1055613458 * a - 0.0638541728 * b).let { it * it * it }
        val s = (lightness - 0.0894841775 * a - 1.2914855480 * b).let { it * it * it }
        return Triple(
            toGamma(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
            toGamma(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
            toGamma(-0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s),
        )
    }

    private fun Triple<Double, Double, Double>.all(predicate: (Double) -> Boolean): Boolean =
        predicate(first) && predicate(second) && predicate(third)

    /**
     * The hue on the familiar wheel, which is the one the brief states and the one the warning
     * is measured on. OkLCH stays internal: it is how the scale is built, not how it is named.
     */
    fun hue(hex: String): Double? {
        val (r, g, b) = toRgb(hex) ?: return null
        val high = max(r, max(g, b))
        val low = min(r, min(g, b))
        val delta = high - low
        if (delta == 0.0) return 0.0
        val raw = when (high) {
            r -> ((g - b) / delta) % SIX
            g -> (b - r) / delta + GREEN_SECTOR
            else -> (r - g) / delta + BLUE_SECTOR
        } * SIXTY
        return if (raw < 0) raw + FULL_TURN else raw
    }

    private const val SIX = 6.0
    private const val SIXTY = 60.0
    private const val GREEN_SECTOR = 2.0
    private const val BLUE_SECTOR = 4.0

    /** Whether a colour sits in the band that has to be justified rather than merely chosen. */
    fun isSuspectHue(hex: String): Boolean = hue(hex)?.let { it in SUSPECT_HUE } ?: false

    /** The hue as it is written in the brief. */
    fun degrees(hue: Double): Int = round(hue).toInt().let { if (abs(it) == FULL_TURN.toInt()) 0 else it }

}
